const mentionPattern =
    /(?:^|[\s([{])@([A-Za-z0-9_]{1,30})$/;

const initializedMentionAutocompletes =
    new WeakSet();

let mentionAutocompleteInstance = 0;

const mentionAutocompleteInstances =
    new Set();

const initializedTextareas =
    new WeakSet();

const getActiveMention = (
    textarea
) => {
    const caretPosition =
        textarea.selectionStart;

    if (
        caretPosition === null
        || caretPosition
            !== textarea.selectionEnd
    ) {
        return null;
    }

    const textBeforeCaret =
        textarea.value.slice(
            0,
            caretPosition
        );

    const match =
        textBeforeCaret.match(
            mentionPattern
        );

    if (!match) {
        return null;
    }

    const matchedText =
        match[0];

    const atOffset =
        matchedText.lastIndexOf('@');

    return {
        query:
            match[1],

        start:
            textBeforeCaret.length
            - matchedText.length
            + atOffset,

        end:
            caretPosition,
    };
};



const createAvatar = (
    member
) => {
    if (member.profile_photo_url) {
        const image =
            document.createElement('img');

        image.src =
            member.profile_photo_url;

        image.alt = '';

        image.loading = 'lazy';

        image.className =
            'h-9 w-9 shrink-0 rounded-full object-cover ring-1 ring-white/10';

        return image;
    }

    const fallback =
        document.createElement('span');

    fallback.className =
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cyan-400/15 text-xs font-black text-cyan-200 ring-1 ring-white/10';

    fallback.textContent =
        member.initials || '?';

    return fallback;
};

const initializeMentionAutocomplete = (
    textarea
) => {
    if (
        initializedTextareas.has(
            textarea
        )
    ) {
        return;
    }

    const initialEndpoint =
        textarea.dataset
            .mentionEndpoint;

    if (!initialEndpoint) {
        return;
    }

    const wrapper =
        textarea.parentElement;

    const results =
        wrapper?.querySelector(
            '[data-mention-results]'
        );

    if (!results) {
        return;
    }

    initializedTextareas.add(
        textarea
    );

mentionAutocompleteInstance++;

const resultsId =
    results.id
    || `mention-results-${mentionAutocompleteInstance}`;

results.id =
    resultsId;

    textarea.setAttribute(
        'autocomplete',
        'off'
    );

    textarea.setAttribute(
        'aria-autocomplete',
        'list'
    );

textarea.setAttribute(
    'aria-controls',
    resultsId
);

    textarea.setAttribute(
        'aria-expanded',
        'false'
    );

    let searchTimeoutId = null;

    let requestNumber = 0;

    let activeMention = null;

    let members = [];

    let activeIndex = -1;

    const closeResults = () => {
        if (searchTimeoutId !== null) {
            window.clearTimeout(
                searchTimeoutId
            );

            searchTimeoutId = null;
        }

        requestNumber++;

        activeMention = null;

        members = [];

        activeIndex = -1;

        results.innerHTML = '';

        results.classList.add(
            'hidden'
        );

        textarea.setAttribute(
            'aria-expanded',
            'false'
        );

        textarea.removeAttribute(
            'aria-activedescendant'
        );
    };

mentionAutocompleteInstances.add({
    textarea,
    results,
    closeResults,
});

    const setActiveIndex = (
        nextIndex
    ) => {
        if (members.length === 0) {
            activeIndex = -1;

            return;
        }

        activeIndex =
            (
                nextIndex
                + members.length
            )
            % members.length;

        const buttons =
            Array.from(
                results.querySelectorAll(
                    '[data-mention-result]'
                )
            );

        buttons.forEach(
            (
                button,
                index
            ) => {
                const selected =
                    index === activeIndex;

                button.classList.toggle(
                    'bg-cyan-50',
                    selected
                );

                button.classList.toggle(
                    'text-cyan-900',
                    selected
                );

                button.classList.toggle(
                    'dark:bg-cyan-400/15',
                    selected
                );

                button.classList.toggle(
                    'dark:text-white',
                    selected
                );

                button.setAttribute(
                    'aria-selected',
                    selected
                        ? 'true'
                        : 'false'
                );
            }
        );

        const activeButton =
            buttons[activeIndex];

        if (activeButton) {
            textarea.setAttribute(
                'aria-activedescendant',
                activeButton.id
            );

            activeButton.scrollIntoView({
                block: 'nearest',
            });
        }
    };

    const insertMember = (
        member
    ) => {
        if (
            !activeMention
            || !member?.username
        ) {
            return;
        }

        const before =
            textarea.value.slice(
                0,
                activeMention.start
            );

        const after =
            textarea.value.slice(
                activeMention.end
            );

        const replacement =
            `@${member.username} `;

        textarea.value =
            before
            + replacement
            + after;

        const caretPosition =
            before.length
            + replacement.length;

        closeResults();

        textarea.focus();

        textarea.setSelectionRange(
            caretPosition,
            caretPosition
        );

        textarea.dispatchEvent(
            new Event(
                'input',
                {
                    bubbles: true,
                }
            )
        );
    };

    const renderResults = (
        receivedMembers
    ) => {
        results.innerHTML = '';

        members =
            Array.isArray(
                receivedMembers
            )
                ? receivedMembers.filter(
                    (member) =>
                        Boolean(
                            member?.username
                        )
                )
                : [];

        if (members.length === 0) {
            const empty =
                document.createElement(
                    'p'
                );

            empty.className =
                'px-4 py-3 text-sm font-semibold text-slate-400';

            empty.textContent =
                'No matching members.';

            results.appendChild(
                empty
            );

            results.classList.remove(
                'hidden'
            );

            textarea.setAttribute(
                'aria-expanded',
                'true'
            );

            return;
        }

        members.forEach(
            (
                member,
                index
            ) => {
                const button =
                    document.createElement(
                        'button'
                    );

                button.type = 'button';

                button.id =
                    `mention-result-${Date.now()}-${index}`;

                button.dataset
                    .mentionResult =
                    String(index);

                button.className =
                    'flex w-full items-center gap-3 px-4 py-3 text-left text-zinc-700 transition hover:bg-cyan-50 hover:text-cyan-900 dark:text-slate-200 dark:hover:bg-cyan-400/15 dark:hover:text-white';

                button.setAttribute(
                    'role',
                    'option'
                );

                button.setAttribute(
                    'aria-selected',
                    'false'
                );

                const text =
                    document.createElement(
                        'span'
                    );

                text.className =
                    'min-w-0';

                const name =
                    document.createElement(
                        'span'
                    );

                name.className =
                    'block truncate text-sm font-black';

                name.textContent =
                    member.name
                    || member.username;

                const username =
                    document.createElement(
                        'span'
                    );

                username.className =
                    'block truncate text-xs font-semibold text-zinc-500 dark:text-slate-500';

                username.textContent =
                    `@${member.username}`;

                text.appendChild(name);

                text.appendChild(
                    username
                );

                button.appendChild(
                    createAvatar(member)
                );

                button.appendChild(text);

                button.addEventListener(
                    'mousedown',
                    (event) => {
                        event.preventDefault();
                    }
                );

                button.addEventListener(
                    'click',
                    () => {
                        insertMember(
                            member
                        );
                    }
                );

                results.appendChild(
                    button
                );
            }
        );

        results.classList.remove(
            'hidden'
        );

        textarea.setAttribute(
            'aria-expanded',
            'true'
        );

        setActiveIndex(0);
    };

    const searchMembers = async (
        mention,
        currentRequestNumber
    ) => {
        try {
            const endpoint =
                textarea.dataset
                    .mentionEndpoint;

            if (!endpoint) {
                closeResults();

                return;
            }

            const url =
                new URL(
                    endpoint,
                    window.location.origin
                );

            url.searchParams.set(
                'q',
                mention.query
            );

            const response =
                await fetch(
                    url.toString(),
                    {
                        method: 'GET',

                        credentials:
                            'same-origin',

                        headers: {
                            Accept:
                                'application/json',

                            'X-Requested-With':
                                'XMLHttpRequest',
                        },
                    }
                );

            if (
                currentRequestNumber
                !== requestNumber
            ) {
                return;
            }

            if (!response.ok) {
                closeResults();

                return;
            }

            const data =
                await response.json();

            if (
                currentRequestNumber
                !== requestNumber
            ) {
                return;
            }

            renderResults(
                data.members
            );
        } catch (error) {
            if (
                currentRequestNumber
                === requestNumber
            ) {
                closeResults();
            }
        }
    };

    const scheduleSearch = () => {
        const mention =
            getActiveMention(
                textarea
            );

        if (!mention) {
            closeResults();

            return;
        }

        activeMention =
            mention;

        if (searchTimeoutId !== null) {
            window.clearTimeout(
                searchTimeoutId
            );
        }

        const currentRequestNumber =
            ++requestNumber;

        searchTimeoutId =
            window.setTimeout(
                () => {
                    searchTimeoutId =
                        null;

                    searchMembers(
                        mention,
                        currentRequestNumber
                    );
                },
                250
            );
    };

    textarea.addEventListener(
        'input',
        scheduleSearch
    );

    textarea.addEventListener(
        'click',
        scheduleSearch
    );

    textarea.addEventListener(
        'keyup',
        (event) => {
            if (
                [
                    'ArrowUp',
                    'ArrowDown',
                    'Enter',
                    'Tab',
                    'Escape',
                ].includes(event.key)
            ) {
                return;
            }

            scheduleSearch();
        }
    );

    textarea.addEventListener(
        'keydown',
        (event) => {
            const visible =
                !results.classList
                    .contains('hidden');

            if (!visible) {
                return;
            }

            if (
                event.key
                === 'ArrowDown'
            ) {
                event.preventDefault();

                setActiveIndex(
                    activeIndex + 1
                );

                return;
            }

            if (
                event.key
                === 'ArrowUp'
            ) {
                event.preventDefault();

                setActiveIndex(
                    activeIndex - 1
                );

                return;
            }

            if (
                (
                    event.key === 'Enter'
                    || event.key === 'Tab'
                )
                && activeIndex >= 0
            ) {
                event.preventDefault();

                insertMember(
                    members[activeIndex]
                );

                return;
            }

            if (
                event.key === 'Escape'
            ) {
                event.preventDefault();

                closeResults();
            }
        }
    );

    textarea
        .closest('form')
        ?.addEventListener(
            'reset',
            () => {
                window.setTimeout(
                    closeResults,
                    0
                );
            }
        );

    
};

document.addEventListener(
    'click',
    (event) => {
        const target =
            event.target;

        if (!(target instanceof Node)) {
            return;
        }

        mentionAutocompleteInstances.forEach(
            ({
                textarea,
                results,
                closeResults,
            }) => {
                if (
                    textarea.contains(target)
                    || results.contains(target)
                ) {
                    return;
                }

                closeResults();
            }
        );
    }
);

export const initializeMentionAutocompletes = (
    root = document
) => {
    root
        .querySelectorAll(
            'textarea[data-mention-autocomplete]'
        )
        .forEach(
            initializeMentionAutocomplete
        );
};

if (
    document.readyState
    === 'loading'
) {
    document.addEventListener(
        'DOMContentLoaded',
        initializeMentionAutocompletes
    );
} else {
    initializeMentionAutocompletes();
}